// Full personal-data export.
//
// The server holds conversations, messages and account info. Uploaded
// attachments are NOT on the server -- they only exist in this browser's
// IndexedDB. Neither half is a complete export on its own, so we zip both.
import JSZip from 'jszip';
import config from '../../config';
import { retrieveAllFiles } from './fileDatabase';

// Matches "[File: brief.pdf](file:1712345-brief.pdf)"; group 2 is the IndexedDB key.
const FILE_REF = /\[File: ([^\]]+)\](?:\(file:([^)]+)\))?/g;

interface ExportedMessage {
  uuid: string;
  role: string;
  content: string;
  model: string | null;
  sources: unknown;
  created_at: string | null;
}

interface ExportedConversation {
  uuid: string;
  title: string | null;
  created_at: string | null;
  updated_at: string | null;
  context_type: string | null;
  context_content: string | null;
  messages: ExportedMessage[];
}

export interface ExportPayload {
  schema_version: number;
  exported_at: string;
  account: { email: string; status: string; [k: string]: unknown };
  usage: { total_credits_used: number; [k: string]: unknown };
  totals: { conversations: number; messages: number };
  conversations: ExportedConversation[];
}

export interface ExportResult {
  conversations: number;
  messages: number;
  files: number;
  missingFiles: string[];
}

/** Make a string safe to use as a path segment inside the ZIP. */
const slugify = (value: string, fallback: string): string => {
  const cleaned = value
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return cleaned || fallback;
};

const formatTimestamp = (value: string | null): string =>
  value ? new Date(value).toLocaleString('id-ID') : 'tanpa tanggal';

const toMarkdown = (conversation: ExportedConversation): string => {
  const lines: string[] = [
    `# ${conversation.title || 'Tanpa judul'}`,
    '',
    `- Dibuat: ${formatTimestamp(conversation.created_at)}`,
    `- Diperbarui: ${formatTimestamp(conversation.updated_at)}`,
    `- Jumlah pesan: ${conversation.messages.length}`,
    '',
  ];

  if (conversation.context_content) {
    lines.push(`## Konteks (${conversation.context_type || 'umum'})`, '', conversation.context_content, '');
  }

  lines.push('---', '');

  conversation.messages.forEach((message) => {
    const who = message.role === 'user' ? 'Kamu' : message.role === 'assistant' ? 'Power AI' : message.role;
    const model = message.model ? ` · ${message.model}` : '';
    lines.push(`## ${who}${model}`, `*${formatTimestamp(message.created_at)}*`, '', message.content || '', '');
  });

  return lines.join('\n');
};

/** IndexedDB keys referenced by any message, mapped to their display name. */
const referencedFileKeys = (conversations: ExportedConversation[]): Map<string, string> => {
  const refs = new Map<string, string>();
  conversations.forEach((conversation) => {
    conversation.messages.forEach((message) => {
      if (!message.content) return;
      // exec loops need a fresh lastIndex per string on a /g regex
      FILE_REF.lastIndex = 0;
      let match = FILE_REF.exec(message.content);
      while (match !== null) {
        const [, displayName, key] = match;
        if (key) refs.set(key, displayName);
        match = FILE_REF.exec(message.content);
      }
    });
  });
  return refs;
};

export const fetchExportPayload = async (): Promise<ExportPayload> => {
  const token = localStorage.getItem('accessToken');
  if (!token) throw new Error('Kamu perlu login dulu untuk mengunduh data.');

  const response = await fetch(`${config.apiUrl}/export/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error('Sesi kamu sudah berakhir. Silakan login lagi lalu coba unduh ulang.');
  }
  if (!response.ok) {
    throw new Error(`Gagal mengambil data dari server (${response.status}).`);
  }

  return response.json();
};

/**
 * Assemble the archive from server data plus whatever attachments this browser
 * still holds. Pure with respect to the network and IndexedDB so it can be
 * tested directly.
 */
export const buildExportZip = (
  payload: ExportPayload,
  storedFiles: Array<{ filename: string; file: Blob }>
): { zip: JSZip; result: ExportResult } => {
  const zip = new JSZip();

  zip.file('data.json', JSON.stringify(payload, null, 2));

  const usedNames = new Set<string>();
  payload.conversations.forEach((conversation, index) => {
    const base = slugify(conversation.title || '', `percakapan-${index + 1}`);
    let name = base;
    let suffix = 2;
    while (usedNames.has(name.toLowerCase())) {
      name = `${base} (${suffix++})`;
    }
    usedNames.add(name.toLowerCase());
    zip.file(`percakapan/${name}.md`, toMarkdown(conversation));
  });

  const storedKeys = new Set(storedFiles.map((row) => row.filename));
  storedFiles.forEach(({ filename, file }) => {
    zip.file(`file/${filename}`, file);
  });

  // A message can reference a file this browser never had (uploaded on another
  // device, or cleared). Tell the user rather than pretend the export is whole.
  const missingFiles: string[] = [];
  referencedFileKeys(payload.conversations).forEach((displayName, key) => {
    if (!storedKeys.has(key)) missingFiles.push(displayName);
  });

  const readme = [
    '# Ekspor data Power AI',
    '',
    `Diunduh: ${new Date().toLocaleString('id-ID')}`,
    `Akun: ${payload.account.email}`,
    '',
    '## Isi',
    '- `data.json`: seluruh data mentah, yaitu akun, pemakaian kredit, percakapan, dan pesan.',
    '- `percakapan/`: satu berkas Markdown per percakapan, enak dibaca.',
    '- `file/`: lampiran yang pernah kamu unggah, diambil dari penyimpanan browser ini.',
    '',
    '## Catatan penting',
    'Lampiran tidak pernah disimpan di server kami, hanya di browser yang kamu pakai',
    'saat mengunggahnya. Jadi folder `file/` hanya berisi lampiran dari browser ini.',
    'Kalau kamu pernah memakai Power AI di perangkat atau browser lain, unduh lagi',
    'dari sana untuk melengkapi arsipmu.',
    '',
    missingFiles.length
      ? `Lampiran yang disebut di percakapan tapi tidak ada di browser ini (${missingFiles.length}):\n` +
        missingFiles.map((name) => `- ${name}`).join('\n')
      : 'Semua lampiran yang disebut di percakapanmu berhasil disertakan.',
    '',
  ].join('\n');
  zip.file('BACA-DULU.md', readme);

  return {
    zip,
    result: {
      conversations: payload.totals.conversations,
      messages: payload.totals.messages,
      files: storedFiles.length,
      missingFiles,
    },
  };
};

/**
 * Fetch, assemble, and hand the ZIP to the browser as a download.
 * An unreadable attachment store degrades to "conversations only" rather than
 * failing the whole export -- partial data beats none.
 */
export const downloadFullExport = async (): Promise<ExportResult> => {
  const payload = await fetchExportPayload();

  let storedFiles: Array<{ filename: string; file: File }> = [];
  try {
    storedFiles = await retrieveAllFiles();
  } catch (error) {
    console.error('Could not read attachments from IndexedDB:', error);
  }

  const { zip, result } = buildExportZip(payload, storedFiles);

  const blob = await zip.generateAsync({ type: 'blob' });
  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `powerai-export-${stamp}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return result;
};
